import { useEffect, useState } from 'react';
import { fetchOrgInfo, type OrgAddressInput, type OrgInfo } from './api';
import { BrandHeader } from './components/BrandHeader';
import { ErrorPage } from './components/ErrorPage';
import { SubmissionForm } from './components/SubmissionForm';
import { useBrand } from './hooks/useBrand';

type AppState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; token: string; org: OrgInfo };

function base64urlDecode(input: string): string {
  const pad = input.length % 4 === 0 ? '' : '='.repeat(4 - (input.length % 4));
  const b64 = input.replace(/-/g, '+').replace(/_/g, '/') + pad;
  return atob(b64);
}

function extractOrgIdFromToken(token: string): string | null {
  const parts = token.split('.');
  if (parts.length !== 2) return null;
  try {
    const json = base64urlDecode(parts[0]);
    const payload = JSON.parse(json) as { org_id?: unknown };
    return typeof payload.org_id === 'string' ? payload.org_id : null;
  } catch {
    return null;
  }
}

export default function App() {
  const { brand, loading: brandLoading } = useBrand();
  const [state, setState] = useState<AppState>({ kind: 'loading' });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (!token) {
      setState({ kind: 'error' });
      return;
    }
    const orgId = extractOrgIdFromToken(token);
    if (!orgId) {
      setState({ kind: 'error' });
      return;
    }

    let cancelled = false;
    fetchOrgInfo(orgId, token)
      .then((org) => {
        if (cancelled) return;
        setState({ kind: 'ready', token, org });
      })
      .catch(() => {
        if (!cancelled) setState({ kind: 'error' });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const contactEmail = brand?.contactEmail ?? '';

  if (brandLoading || state.kind === 'loading') {
    return (
      <div className="min-h-full">
        <BrandHeader brand={brand} />
        <main className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
          <div
            role="status"
            aria-live="polite"
            className="flex items-center gap-3 text-slate-600"
          >
            <span
              className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-transparent"
              aria-hidden
            />
            Loading…
          </div>
        </main>
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div className="min-h-full">
        <BrandHeader brand={brand} />
        <ErrorPage contactEmail={contactEmail} />
      </div>
    );
  }

  const { org } = state;
  const initial: OrgAddressInput = {
    orgAddress: org.orgAddress,
    orgAddress2: org.orgAddress2,
    orgCity: org.orgCity,
    orgState: org.orgState,
    orgZip: org.orgZip,
    orgCountry: org.orgCountry || 'USA',
  };

  return (
    <div className="min-h-full">
      <BrandHeader brand={brand} />
      <main className="mx-auto max-w-3xl space-y-6 px-4 py-8 sm:px-6 sm:py-10">
        <SubmissionForm
          orgId={org.orgId}
          orgName={org.orgName}
          token={state.token}
          initial={initial}
          contactEmail={contactEmail}
        />
      </main>
    </div>
  );
}
